import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Buildings,
  Calendar,
  CircleNotch,
  Clock,
  FlowArrow,
  TrendUp,
  User,
  WarningCircle,
  X
} from '@phosphor-icons/react';

const CreateProcessModal = ({ template, onClose, onSuccess, activeOrganization }) => {
  const [formData, setFormData] = useState({
    name: template.name,
    description: template.description,
    department: '',
    owner: '',
    frequency: '',
    volume: '',
    cycle_time: ''
  });
  
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!activeOrganization) {
      setError('Please select an organization first');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Format template steps
      const formattedSteps = (template.steps || []).map((step, idx) => ({
        step_number: (idx + 1).toString(),
        description: typeof step === 'string' ? step : (step.description || step.title || 'Step description'),
        status: 'pending'
      }));

      // Map category to valid enum
      const validCategories = ['sales', 'support', 'operations', 'finance', 'hr', 'marketing', 'product', 'compliance'];
      const categoryMap = {
        'construction': 'operations',
        'trades': 'operations',
        'services': 'operations',
        'manufacturing': 'operations',
        'retail': 'operations',
        'hospitality': 'operations',
        'healthcare': 'support',
        'education': 'support',
        'logistics': 'operations',
        'real_estate': 'operations'
      };
      
      let category = template.category?.toLowerCase() || 'operations';
      if (!validCategories.includes(category)) {
        category = categoryMap[category] || 'operations';
      }

      const response = await fetch(`${BACKEND_URL}/api/process/processes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-ID': activeOrganization.organization_id
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          category: category,
          template_id: template.template_id,
          current_state: 'Starting from template',
          pain_points: [],
          steps: formattedSteps,
          stakeholders: [],
          department: formData.department || null,
          owner_name: formData.owner || null,  // Match Process model field name
          frequency: formData.frequency || null,
          volume_per_period: formData.volume ? parseInt(formData.volume) : null,  // Match Process model
          avg_cycle_time_minutes: formData.cycle_time ? parseFloat(formData.cycle_time) : null  // Match Process model
        })
      });

      if (response.ok) {
        const result = await response.json();
        onSuccess(result.process_id);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create process');
      }
    } catch (err) {
      console.error('Error creating process:', err);
      setError(err.message || 'Failed to create process. Please try again.');
      setCreating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.8)]"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="glass rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 glass rounded-t-2xl p-6 border-b border-[rgba(255,255,255,0.1)] flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-2">Create Process from Template</h2>
            <p className="text-white/60 text-sm">Review and customize the process details before creating</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors ml-4"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3">
              <WarningCircle size={20} className="text-[#EF4444]" />
              <p className="text-[#EF4444] text-sm">{error}</p>
            </div>
          )}

          {/* Process Name */}
          <div>
            <label className="block text-white font-semibold mb-2">
              Process Name <span className="text-[#EF4444]">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl glass border border-[rgba(255,255,255,0.1)] text-white placeholder-white/40 focus:outline-none focus:border-[#6366F1]"
              placeholder="Enter process name"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-white font-semibold mb-2">
              Description / Objective <span className="text-[#EF4444]">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              required
              rows={3}
              className="w-full px-4 py-3 rounded-xl glass border border-[rgba(255,255,255,0.1)] text-white placeholder-white/40 focus:outline-none focus:border-[#6366F1] resize-none"
              placeholder="Describe the process objective"
            />
          </div>

          {/* Optional Fields Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Department */}
            <div>
              <label className="block text-white font-semibold mb-2 flex items-center gap-2">
                <Buildings size={16} />
                Department
              </label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => handleChange('department', e.target.value)}
                className="w-full px-4 py-3 rounded-xl glass border border-[rgba(255,255,255,0.1)] text-white placeholder-white/40 focus:outline-none focus:border-[#6366F1]"
                placeholder="e.g., Sales, Operations"
              />
            </div>

            {/* Owner */}
            <div>
              <label className="block text-white font-semibold mb-2 flex items-center gap-2">
                <User size={16} />
                Owner / Role
              </label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => handleChange('owner', e.target.value)}
                className="w-full px-4 py-3 rounded-xl glass border border-[rgba(255,255,255,0.1)] text-white placeholder-white/40 focus:outline-none focus:border-[#6366F1]"
                placeholder="e.g., Sales Manager"
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-white font-semibold mb-2 flex items-center gap-2">
                <Calendar size={16} />
                Frequency
              </label>
              <input
                type="text"
                value={formData.frequency}
                onChange={(e) => handleChange('frequency', e.target.value)}
                className="w-full px-4 py-3 rounded-xl glass border border-[rgba(255,255,255,0.1)] text-white placeholder-white/40 focus:outline-none focus:border-[#6366F1]"
                placeholder="e.g., Daily, Weekly"
              />
            </div>

            {/* Volume */}
            <div>
              <label className="block text-white font-semibold mb-2 flex items-center gap-2">
                <TrendUp size={16} />
                Volume per Period
              </label>
              <input
                type="number"
                value={formData.volume}
                onChange={(e) => handleChange('volume', e.target.value)}
                className="w-full px-4 py-3 rounded-xl glass border border-[rgba(255,255,255,0.1)] text-white placeholder-white/40 focus:outline-none focus:border-[#6366F1]"
                placeholder="e.g., 50"
              />
            </div>

            {/* Cycle Time */}
            <div className="md:col-span-2">
              <label className="block text-white font-semibold mb-2 flex items-center gap-2">
                <Clock size={16} />
                Avg. Cycle Time (minutes)
              </label>
              <input
                type="number"
                value={formData.cycle_time}
                onChange={(e) => handleChange('cycle_time', e.target.value)}
                className="w-full px-4 py-3 rounded-xl glass border border-[rgba(255,255,255,0.1)] text-white placeholder-white/40 focus:outline-none focus:border-[#6366F1]"
                placeholder="e.g., 120 (for 2 hours)"
              />
            </div>
          </div>

          {/* Process Steps Preview */}
          <div>
            <label className="block text-white font-semibold mb-3">Pre-filled Process Steps</label>
            <div className="space-y-2 p-4 rounded-xl glass border border-[rgba(255,255,255,0.1)]">
              {template.steps && template.steps.length > 0 ? (
                template.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-white/80 text-sm">
                    <span className="text-[#6366F1] font-semibold">{idx + 1}.</span>
                    <span>{typeof step === 'string' ? step : (step.description || step.title || 'Step description')}</span>
                  </div>
                ))
              ) : (
                <p className="text-white/40 text-sm">No steps defined in template</p>
              )}
              <p className="text-white/40 text-xs mt-3 italic">
                You can edit these steps later on the process detail page
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-[rgba(255,255,255,0.1)]">
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="px-6 py-3 rounded-xl glass text-white font-semibold hover:bg-[rgba(255,255,255,0.05)] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold flex items-center justify-center gap-2 hover:scale-105 transition-transform disabled:opacity-50 disabled:scale-100"
            >
              {creating ? (
                <>
                  <CircleNotch size={20} className="animate-spin" />
                  Creating Process...
                </>
              ) : (
                <>
                  <FlowArrow size={20} />
                  Create Process
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default CreateProcessModal;
